import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User as UserIcon, ArrowRight, Gift } from "lucide-react";
import Onboarding from "@/components/Onboarding";
import Seo from "@/components/Seo";

const LOGIN_SEO = (
  <Seo
    title="BIZARRE AI — IA pour répondre, analyser, créer, débattre et connaître ses droits"
    description="IA pour répondre, analyser, créer du contenu, débattre et connaître ses droits."
    path="/"
  />
);

export default function Login() {
  const { loginWithToken, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteCode = params.get("invite");
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem("bizarre_onboarded") && !params.get("invite")
  );
  const [mode, setMode] = useState(inviteCode ? "register" : "login");
  const [invitePlan, setInvitePlan] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [inviteOnly, setInviteOnly] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", account_type: "adult", birth_year: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setInviteOnly(!!data.invite_only)).catch(() => {});
    if (inviteCode) {
      localStorage.setItem("bizarre_pending_invite", inviteCode);
      api.get(`/invite/${inviteCode}`).then(({ data }) => {
        if (data.valid) { setInvitePlan(data.plan); setInviteError(null); }
        else { setInvitePlan(null); setInviteError(data.reason || "Ce lien d'invitation n'est plus valide."); }
      }).catch(() => setInviteError("Ce lien d'invitation n'est plus valide."));
    }
  }, [inviteCode]);

  const finishOnboarding = (chosenMode) => {
    localStorage.setItem("bizarre_onboarded", "1");
    setMode(chosenMode);
    setShowOnboarding(false);
  };

  if (showOnboarding) return (<>{LOGIN_SEO}<Onboarding onDone={finishOnboarding} /></>);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password,
            invite_code: inviteCode || undefined, account_type: form.account_type,
            birth_year: form.birth_year ? Number(form.birth_year) : undefined };
      const { data } = await api.post(path, payload);
      loginWithToken(data.token, data.user);
      toast.success(mode === "login" ? "Bienvenue." : "Compte créé.");
      navigate("/");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6 relative overflow-hidden">
      {LOGIN_SEO}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{ background: "radial-gradient(600px circle at 30% 20%, rgba(212,175,55,0.25), transparent 60%)" }} />
      <div className="relative w-full max-w-md fade-up">
        <div className="flex flex-col items-center gap-3 mb-8">
          <div className="flex items-center gap-2 justify-center">
            <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center">
              <span className="text-black font-bold font-display">B</span>
            </div>
            <span className="font-display text-2xl tracking-tight">BIZARRE <span className="gold-text">AI</span></span>
          </div>
          <p className="text-white/45 text-sm text-center max-w-xs leading-relaxed">
            L'IA qui vous aide à répondre avec intelligence, créer du contenu et maîtriser l'art de convaincre.
          </p>
        </div>

        <div className="card-surface p-8 noise relative">
          {invitePlan && (
            <div data-testid="invite-banner" className="mb-5 flex items-center gap-3 rounded-xl border border-gold/40 bg-gradient-to-r from-[#BF953F]/15 to-transparent p-3">
              <Gift size={18} className="text-gold shrink-0" />
              <p className="text-sm text-white/85">
                Vous êtes invité(e) en bêta avec un accès <span className="text-gold font-medium">{invitePlan === "pro" ? "Pro" : invitePlan === "premium" ? "Premium" : "Gratuit"}</span> offert. Créez votre compte pour en profiter.
              </p>
            </div>
          )}
          {inviteError && (
            <div data-testid="invite-error" className="mb-5 rounded-xl border border-red-500/30 bg-red-500/[0.08] p-3 text-sm text-red-200">
              {inviteError} Demandez un nouveau lien à l'administrateur.
            </div>
          )}
          <h1 className="font-display text-2xl mb-1 tracking-tight">
            {mode === "login" ? "Ravi de vous accueillir" : "Créez votre compte"}
          </h1>
          <p className="text-white/40 text-sm mb-6">
            Répondez avec intelligence. Convainquez avec des arguments. Créez du contenu qui marque les esprits.
          </p>

          <button onClick={googleLogin} data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 bg-white text-black rounded-full py-2.5 font-medium hover:bg-gray-200 transition-colors mb-4">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
            Continuer avec Google
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-white/30 text-xs">ou</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="space-y-2">
                <select data-testid="account-type-select" value={form.account_type}
                  onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm">
                  <option value="adult">Je suis un adulte</option>
                  <option value="parent">Je suis un parent</option>
                  <option value="young">Je suis un jeune (moins de 18 ans)</option>
                </select>
                <input data-testid="birth-year-input" type="number" inputMode="numeric" placeholder="Année de naissance (facultatif)"
                  value={form.birth_year} min={1900} max={2025}
                  onChange={(e) => setForm({ ...form, birth_year: e.target.value })}
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm" />
                <p className="text-white/35 text-[11px] px-1">Cela nous aide à protéger les plus jeunes. Les comptes enfants et adolescents n'ont pas accès aux espaces publics.</p>
              </div>
            )}
            {mode === "register" && (
              <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-3">
                <UserIcon size={16} className="text-white/40" />
                <input data-testid="name-input" required placeholder="Nom" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="flex-1 bg-transparent py-2.5 outline-none text-sm" />
              </div>
            )}
            <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-3">
              <Mail size={16} className="text-white/40" />
              <input data-testid="email-input" type="email" required placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="flex-1 bg-transparent py-2.5 outline-none text-sm" />
            </div>
            <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-3">
              <Lock size={16} className="text-white/40" />
              <input data-testid="password-input" type="password" required placeholder="Mot de passe" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="flex-1 bg-transparent py-2.5 outline-none text-sm" />
            </div>
            <button type="submit" disabled={busy} data-testid="submit-auth-button"
              className="w-full flex items-center justify-center gap-2 gold-gradient text-black rounded-full py-2.5 font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? "..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
              {!busy && <ArrowRight size={16} />}
            </button>
          </form>

          <p className="text-center text-sm text-white/40 mt-5">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-gold hover:underline">
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
        </div>
        <p className="text-center text-white/25 text-xs mt-5">
          <Link to="/privacy" data-testid="privacy-link" className="hover:text-white/50">Politique de confidentialité</Link>
        </p>
      </div>
    </div>
  );
}
